import { Alert, Card, CardBody, Col, Container, Row, Form, Button as BsButton } from "react-bootstrap";
import { PageTitle } from "./Controls/PageTitle";
import { StripeElementsOptions } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "./Data/ApiStripe";
import { appearanceOptions, stripeFonts } from "./Controls/StripeAppearance";
import { SettleInitResp, Facility, FacilityParkerBalance } from "./Data/ApiTypes";
import { cents, showMoney, toCents } from "./Data/Money";
import { useParams, useLoaderData, useRouteLoaderData, useNavigate } from "react-router-dom";
import { StripeSettleCheckoutForm } from "./StripeSettleCheckoutForm";
import { showInstantLocalDate } from "./Data/Date";
import { equal } from "dinero.js";
import React, { useState, useEffect, useRef } from "react";
import { CreditCard, Bank } from "react-bootstrap-icons";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { parseSettleInitResp } from "./Data/ApiParse";
import { isFeatureEnabled, FEATURES } from './Data/Features';
import { Button } from "./Controls/Button";
import { MonthlyPriceBreakdown } from "./Controls/MonthlyPriceBreakdown";

enum PaymentMethod {
    Card = "card",
    ACH = "ach"
}

enum PaymentAmount {
    Full = "full",
    Partial = "partial"
}

const MIN_PAYMENT_CENTS = 10000; // $100.00

export function UserSettlePage() {
    const { facilityId } = useParams();
    const facility = useLoaderData() as Facility;
    const balances = useRouteLoaderData("/user") as FacilityParkerBalance[];
    const facilityBalance = balances.find(b => b.facility.facilityId === parseInt(facilityId || "0"));
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PaymentMethod.Card);
    const [paymentAmount, setPaymentAmount] = useState<PaymentAmount>(PaymentAmount.Full);
    const [partialAmountDollars, setPartialAmountDollars] = useState<string>("");
    const [settle, setSettle] = useState<SettleInitResp | null>(null);
    const [loading, setLoading] = useState(false);
    const [paymentVis, setPaymentVis] = useState<"hidden" | "visible">("hidden");
    const [settleError, setSettleError] = useState<string | null>(null);
    const achEnabled = isFeatureEnabled(facility, FEATURES.ACH);
    const partialPaymentsEnabled = isFeatureEnabled(facility, FEATURES.ALLOW_PARTIAL_PAYMENTS) && facilityBalance && toCents(facilityBalance.balance) >= MIN_PAYMENT_CENTS;
    const amountInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    
    useEffect(() => {
        if (paymentAmount === PaymentAmount.Partial && amountInputRef.current) {
            amountInputRef.current.focus();
            amountInputRef.current.select();
        }
    }, [paymentAmount]);

    const getPaymentAmountInCents = (): number | null => {
        if (paymentAmount === PaymentAmount.Full) {
            return null; // null indicates full payment
        }
        const dollars = parseFloat(partialAmountDollars);
        if (isNaN(dollars)) return null;
        return Math.round(dollars * 100);
    };

    const isPartialAmountValid = (): boolean => {
        if (paymentAmount === PaymentAmount.Full) return true;
        const amountInCents = getPaymentAmountInCents();
        if (amountInCents === null) return false;
        if (amountInCents < MIN_PAYMENT_CENTS) return false;
        if (facilityBalance && amountInCents > toCents(facilityBalance.balance)) return false;
        return true;
    };

    const selectedPaymentAmountInCents = paymentAmount === PaymentAmount.Full
        ? facilityBalance ? toCents(facilityBalance.balance) : null
        : getPaymentAmountInCents();
    const maxCreditCardAmountInCents = facility.maxCreditCardAmountInCents ?? facilityBalance?.facility.maxCreditCardAmountInCents ?? null;
    const creditCardLimitExceeded = maxCreditCardAmountInCents !== null
        && selectedPaymentAmountInCents !== null
        && selectedPaymentAmountInCents > maxCreditCardAmountInCents;
    const paymentBlocked = creditCardLimitExceeded && !achEnabled;

    useEffect(() => {
        if (creditCardLimitExceeded && achEnabled && paymentMethod === PaymentMethod.Card) {
            setPaymentMethod(PaymentMethod.ACH);
        }
    }, [creditCardLimitExceeded, achEnabled, paymentMethod]);

    useEffect(() => {
        setSettleError(null);
    }, [paymentAmount, partialAmountDollars, paymentMethod]);

    const handlePaymentMethodSelect = async () => {
        if (!facilityId) return;
        if (paymentBlocked) {
            setSettleError("This payment amount exceeds the facility's credit card limit, and ACH is not available. Please contact the facility.");
            return;
        }
        
        setSettleError(null);
        setLoading(true);
        const paymentAmountCents = getPaymentAmountInCents();
        const mustUseAch = creditCardLimitExceeded && achEnabled;
        
        const init = {
            facilityId: parseInt(facilityId),
            isAch: mustUseAch || paymentMethod === PaymentMethod.ACH,
            amountInCents: paymentAmountCents ?? undefined
        } as const;
        
        Api.settleInit(init).then(
            res => processResult(res,
                val => {
                    const settleResp = parseSettleInitResp(val);
                    setSettle(settleResp);
                    setPaymentVis(!equal(settleResp.price, cents(0)) ? "visible" : "hidden");
                    setLoading(false);
                },
                err => {
                    console.error(err);
                    setSettleError(typeof err === "string" ? err : "Unable to initialize payment. Please try again.");
                    setLoading(false);
                }
            )
        );
    };

    if (!settle) {
        return (
            <Container fluid>
                <Row>
                    <Col>
                        <div className="d-flex align-items-center gap-3">
                            <BsButton variant="link" className="p-0" onClick={() => navigate(-1)}>← Back</BsButton>
                            <PageTitle>
                                Pay Off Balance
                            </PageTitle>
                        </div>
                        
                        <Form>
                            {/* Step 1: Payment Amount */}
                            {partialPaymentsEnabled && (
                                <Card className="mb-4 mt-2 shadow border-0">
                                    <CardBody className="p-4">
                                        <div className="d-flex align-items-center mb-4">
                                            <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3" style={{ width: '36px', height: '36px', fontSize: '1rem', fontWeight: 'bold' }}>
                                                1
                                            </div>
                                            <h5 className="mb-0">Payment Amount</h5>
                                        </div>
                                    
                                    <div className="d-flex flex-column gap-3">
                                        <div className="d-flex align-items-center gap-3">
                                            <Form.Check
                                                type="radio"
                                                id="full-payment"
                                                name="payment-amount"
                                                checked={paymentAmount === PaymentAmount.Full}
                                                onChange={() => setPaymentAmount(PaymentAmount.Full)}
                                            />
                                            <div 
                                                onClick={() => setPaymentAmount(PaymentAmount.Full)}
                                                style={{ cursor: 'pointer', flex: 1 }}
                                                className={`p-3 border rounded-3 transition-all ${paymentAmount === PaymentAmount.Full ? 'border-primary border-2 bg-primary bg-opacity-10' : 'border-2'}`}
                                            >
                                                <div className="fw-bold">Pay Full Balance</div>
                                                {facilityBalance && (
                                                    <div className="text-muted mt-1 small">
                                                        Total Outstanding: <span className="fw-semibold text-dark">{showMoney(facilityBalance.balance)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="d-flex align-items-center gap-3">
                                            <Form.Check
                                                type="radio"
                                                id="partial-payment"
                                                name="payment-amount"
                                                checked={paymentAmount === PaymentAmount.Partial}
                                                onChange={() => setPaymentAmount(PaymentAmount.Partial)}
                                            />
                                            <div 
                                                onClick={() => setPaymentAmount(PaymentAmount.Partial)}
                                                style={{ cursor: 'pointer', flex: 1 }}
                                                className={`p-3 border rounded-3 transition-all ${paymentAmount === PaymentAmount.Partial ? 'border-primary border-2 bg-primary bg-opacity-10' : 'border-2'}`}
                                            >
                                                <div className="fw-bold">Pay Specific Amount</div>
                                                <div className="text-muted mt-1 small">Minimum payment: $100.00</div>
                                                {paymentAmount === PaymentAmount.Partial && (
                                                    <div className="mt-3 pt-3 border-top">
                                                        <Form.Group>
                                                            <Form.Label className="fw-semibold small">Enter Amount</Form.Label>
                                                            <div className="input-group">
                                                                <span className="input-group-text bg-light">$</span>
                                                            <Form.Control
                                                                ref={amountInputRef}
                                                                type="number"
                                                                step="0.01"
                                                                min="100.00"
                                                                max={facilityBalance ? (toCents(facilityBalance.balance) / 100).toFixed(2) : undefined}
                                                                value={partialAmountDollars}
                                                                onChange={(e) => setPartialAmountDollars(e.target.value)}
                                                                placeholder="100.00"
                                                                isInvalid={partialAmountDollars !== "" && !isPartialAmountValid()}
                                                            />
                                                            </div>
                                                            {partialAmountDollars !== "" && !isPartialAmountValid() && (
                                                                <Form.Text className="text-danger d-block mt-2">
                                                                    {getPaymentAmountInCents() === null 
                                                                        ? "Please enter a valid amount"
                                                                        : getPaymentAmountInCents()! < MIN_PAYMENT_CENTS
                                                                        ? "Minimum payment is $100.00"
                                                                        : "Amount exceeds your balance"}
                                                                </Form.Text>
                                                            )}
                                                        </Form.Group>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                            )}

                            {/* Step 2: Payment Method */}
                            <Card className={`mb-4 shadow border-0 ${!partialPaymentsEnabled ? 'mt-2' : ''}`}>
                                <CardBody className="p-4">
                                    <div className="d-flex align-items-center mb-4">
                                        {partialPaymentsEnabled && (
                                            <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3" style={{ width: '36px', height: '36px', fontSize: '1rem', fontWeight: 'bold' }}>
                                                2
                                            </div>
                                        )}
                                        <h5 className="mb-0">Choose Payment Method</h5>
                                    </div>
                                    
                                    <div className="d-flex flex-column gap-3">
                                        {creditCardLimitExceeded && maxCreditCardAmountInCents !== null && (
                                            <div className="d-flex align-items-start gap-3">
                                                <div style={{ width: "1.5rem", flexShrink: 0 }} />
                                                <div className="small text-muted px-3 py-2 rounded-3 bg-light border flex-grow-1">
                                                    {achEnabled
                                                        ? `Payments over ${showMoney(cents(maxCreditCardAmountInCents))} must use ACH.`
                                                        : "This amount exceeds the card limit, and ACH is not available. Please contact the facility."}
                                                </div>
                                            </div>
                                        )}

                                        {!creditCardLimitExceeded && (
                                            <div className="d-flex align-items-center gap-3">
                                                <Form.Check
                                                    type="radio"
                                                    id="card-payment"
                                                    name="payment-method"
                                                    checked={paymentMethod === PaymentMethod.Card}
                                                    onChange={() => setPaymentMethod(PaymentMethod.Card)}
                                                />
                                                <div 
                                                    onClick={() => setPaymentMethod(PaymentMethod.Card)}
                                                    style={{ cursor: 'pointer', flex: 1 }}
                                                    className={`p-3 border rounded-3 transition-all ${paymentMethod === PaymentMethod.Card ? 'border-primary border-2 bg-primary bg-opacity-10' : 'border-2'}`}
                                                >
                                                    <div className="d-flex align-items-center gap-3">
                                                        <CreditCard size={24} className="text-primary" />
                                                        <div>
                                                            <div className="fw-bold">Credit/Debit Card</div>
                                                            <div className="small text-muted">Pay with your card or digital wallet</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {achEnabled && (
                                            <div className="d-flex align-items-center gap-3">
                                                <Form.Check
                                                    type="radio"
                                                    id="ach-payment"
                                                    name="payment-method"
                                                    checked={paymentMethod === PaymentMethod.ACH}
                                                    onChange={() => setPaymentMethod(PaymentMethod.ACH)}
                                                />
                                                <div 
                                                    onClick={() => setPaymentMethod(PaymentMethod.ACH)}
                                                    style={{ cursor: 'pointer', flex: 1 }}
                                                    className={`p-3 border rounded-3 transition-all ${paymentMethod === PaymentMethod.ACH ? 'border-primary border-2 bg-primary bg-opacity-10' : 'border-2'}`}
                                                >
                                                    <div className="d-flex align-items-center gap-3">
                                                        <Bank size={24} className="text-primary" />
                                                        <div>
                                                            <div className="fw-bold">Bank Transfer (ACH)</div>
                                                            <div className="small text-muted">Pay directly from your bank account</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>

                            {settleError && (
                                <Alert variant="danger">{settleError}</Alert>
                            )}

                            {/* Continue Button */}
                            <div className="d-grid mb-3">
                                <Button className="btn-main"
                                    loading={loading}
                                    onClick={handlePaymentMethodSelect}
                                    disabled={loading || !isPartialAmountValid() || paymentBlocked}
                                >
                                   Continue to Payment
                                </Button>
                            </div>
                        </Form>
                    </Col>
                </Row>
            </Container>
        );
    }

    const msg = equal(settle.price, cents(0)) ? "You don't owe anything to this facility at this time." : undefined;
    const orders = settle.orders;
    const facilityInfo = settle.facility;
    const options: StripeElementsOptions = {
        clientSecret: settle.paymentIntentClientSecret,
        customerSessionClientSecret: settle.customerSessionClientSecret,
        appearance: appearanceOptions,
        fonts: stripeFonts
    };

    return (
        <Container fluid>
            <Row>
                <Col>
                    <div className="d-flex align-items-center gap-3">
                        <BsButton
                            variant="link"
                            className="p-0"
                            onClick={() => {
                                setSettle(null);
                                setPaymentVis("hidden");
                            }}
                        >
                            ← Back
                        </BsButton>
                        <PageTitle>
                            Pay Outstanding Balance
                        </PageTitle>
                    </div>
                    <Container className="g-0">
                        <Row>
                            <Col xs={12} className="fs-4">
                                {facilityInfo.name}
                            </Col>
                            <Col xs={12}>
                                <p className="mt-2">
                                    This page allows you to settle your balance right.
                                </p>
                                <p>
                                    To set up your account for autopay, go to Plans, then click on your plan, then click Add Payment Method.
                                </p>
                            </Col>
                            <Col xs={12} className="fs-6">
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th scope="col">Order</th>
                                            <th scope="col"></th>
                                            <th scope="col"></th>
                                            <th scope="col" className="d-none d-md-table-cell">Price</th>
                                            <th scope="col" className="d-none d-md-table-cell">Paid</th>
                                            <th scope="col">Due</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orders.map(o => (
                                            <tr key={o.orderId}>
                                                <td>#{o.orderId}</td>
                                                <td>{showInstantLocalDate(o.createdWhen)}</td>
                                                <td>{o.summary}</td>
                                                <td className="d-none d-md-table-cell">{showMoney(o.balance.totalDebt)}</td>
                                                <td className="d-none d-md-table-cell">{showMoney(o.balance.totalPaid)}</td>
                                                <td>{showMoney(o.balance.balance)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12}>
                                <div className="mb-4">
                                    <MonthlyPriceBreakdown 
                                        price={settle.price}
                                        fees={settle.fees}
                                        showTotalLabel="Today's Payment"
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col>{msg}</Col>
                        </Row>
                    </Container>
                    <br />
                    <div style={{ visibility: paymentVis }}>
                        {!equal(settle.price, cents(0)) && (
                            <>
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm" 
                                    className="mb-4 px-3" 
                                    onClick={() => {
                                        setSettle(null);
                                        setPaymentVis("hidden");
                                    }}
                                >
                                    Back To Payment Type
                                </Button>
                                <Elements stripe={stripePromise} options={options}>
                                    <StripeSettleCheckoutForm initResp={settle} />
                                </Elements>
                            </>
                        )}
                    </div>
                </Col>
            </Row>
        </Container>
    );
}